// Hướng dẫn khi điểm danh không lấy được vị trí.
// Trình duyệt không cho trang web hỏi lại quyền, nên chỉ có thể chỉ đường vào cài đặt.

import type { LocationErrorReason } from './geolocation';

export type BrowserPlatform =
    | 'in-app'
    | 'ios-safari'
    | 'ios-chrome'
    | 'android-chrome'
    | 'mac-safari'
    | 'desktop-chrome'
    | 'firefox'
    | 'other';

export function detectBrowserPlatform(userAgent: string, maxTouchPoints = 0): BrowserPlatform {
    const ua = userAgent;
    // Trình duyệt bên trong Zalo, Facebook, Messenger, Instagram, TikTok thường không xin được quyền vị trí.
    if (/Zalo|FBAN|FBAV|FB_IAB|Messenger|Instagram|musical_ly|TikTok|Line\//i.test(ua)) return 'in-app';
    // iPad đời mới báo mình là Mac; phân biệt bằng màn hình cảm ứng.
    const isIOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
    if (isIOS) return /CriOS|EdgiOS|FxiOS/.test(ua) ? 'ios-chrome' : 'ios-safari';
    if (/Android/.test(ua)) return /Firefox/.test(ua) ? 'firefox' : 'android-chrome';
    if (/Firefox/.test(ua)) return 'firefox';
    if (/Chrome|Chromium|Edg\//.test(ua)) return 'desktop-chrome';
    if (/Macintosh/.test(ua) && /Safari/.test(ua)) return 'mac-safari';
    return 'other';
}

export interface LocationHelpGuide {
    platform: BrowserPlatform;
    title: string;
    // Trường hợp lỡ bấm "Không cho phép": trang web bị chặn trong trình duyệt.
    site: string[];
    // Trường hợp điện thoại/máy tính không cho trình duyệt dùng vị trí, hoặc tắt định vị.
    device: string[];
    // iPhone/Safari thường chỉ nhận quyền mới sau khi tải lại trang.
    needsReload: boolean;
}

const OPEN_IN_BROWSER = [
    'Trình duyệt bên trong Zalo, Facebook, Messenger thường không lấy được vị trí.',
    'Bấm nút ⋯ (góc trên) và chọn "Mở bằng trình duyệt" (Safari hoặc Chrome).',
    'Đăng nhập lại và điểm danh, chọn "Cho phép" khi được hỏi vị trí.',
];

export const LOCATION_HELP: Record<BrowserPlatform, LocationHelpGuide> = {
    'in-app': {
        platform: 'in-app',
        title: 'Đang mở trong Zalo/Facebook',
        site: OPEN_IN_BROWSER,
        device: OPEN_IN_BROWSER,
        needsReload: false,
    },
    'ios-safari': {
        platform: 'ios-safari',
        title: 'iPhone/iPad – Safari',
        site: [
            'Bấm biểu tượng "aA" (hoặc ☰) ở thanh địa chỉ, chọn "Cài đặt trang web".',
            'Ở mục "Vị trí", chọn "Hỏi" hoặc "Cho phép".',
            'Bấm "Tải lại trang".',
        ],
        device: [
            'Mở Cài đặt → Quyền riêng tư & Bảo mật → Dịch vụ định vị, bật lên.',
            'Kéo xuống chọn "Trang web Safari" → "Khi dùng ứng dụng", bật "Vị trí chính xác".',
            'Quay lại đây và bấm "Tải lại trang".',
        ],
        needsReload: true,
    },
    'ios-chrome': {
        platform: 'ios-chrome',
        title: 'iPhone/iPad – Chrome',
        site: [
            'Bấm ⋯ ở góc dưới → Cài đặt → Quyền riêng tư và bảo mật → Cài đặt trang web → Vị trí.',
            'Cho phép trang này (hoặc xoá trang này khỏi danh sách chặn).',
            'Bấm "Tải lại trang".',
        ],
        device: [
            'Mở Cài đặt → Quyền riêng tư & Bảo mật → Dịch vụ định vị, bật lên.',
            'Mở Cài đặt → Chrome → Vị trí → "Khi dùng ứng dụng", bật "Vị trí chính xác".',
            'Quay lại đây và bấm "Tải lại trang".',
        ],
        needsReload: true,
    },
    'android-chrome': {
        platform: 'android-chrome',
        title: 'Android – Chrome',
        site: [
            'Bấm biểu tượng bên trái địa chỉ trang (ổ khoá hoặc ⚙), chọn "Quyền".',
            'Bấm "Đặt lại quyền" (hoặc chọn "Vị trí" → "Cho phép").',
            'Bấm "Thử lại" và chọn "Cho phép" khi trình duyệt hỏi vị trí.',
        ],
        device: [
            'Kéo thanh thông báo xuống, bật "Vị trí".',
            'Mở Cài đặt → Ứng dụng → Chrome → Quyền → Vị trí → "Chỉ cho phép khi đang dùng ứng dụng", bật "Dùng vị trí chính xác".',
            'Quay lại đây và bấm "Thử lại".',
        ],
        needsReload: false,
    },
    'mac-safari': {
        platform: 'mac-safari',
        title: 'Máy Mac – Safari',
        site: [
            'Trên thanh menu chọn Safari → Cài đặt… → tab "Trang web" → "Vị trí".',
            'Tìm trang này và chọn "Cho phép".',
            'Bấm "Tải lại trang".',
        ],
        device: [
            'Mở Cài đặt hệ thống → Quyền riêng tư & Bảo mật → Dịch vụ định vị.',
            'Bật Dịch vụ định vị và bật cho Safari.',
            'Quay lại đây và bấm "Tải lại trang".',
        ],
        needsReload: true,
    },
    'desktop-chrome': {
        platform: 'desktop-chrome',
        title: 'Máy tính – Chrome/Edge',
        site: [
            'Bấm biểu tượng bên trái địa chỉ trang (ổ khoá hoặc ⚙).',
            'Bấm "Đặt lại quyền" (hoặc bật "Vị trí").',
            'Bấm "Thử lại" và chọn "Cho phép" khi trình duyệt hỏi vị trí.',
        ],
        device: [
            'Windows: Cài đặt → Quyền riêng tư & bảo mật → Vị trí, bật "Dịch vụ vị trí" và "Cho phép ứng dụng máy tính truy cập vị trí".',
            'Mac: Cài đặt hệ thống → Quyền riêng tư & Bảo mật → Dịch vụ định vị, bật cho Chrome/Edge.',
            'Quay lại đây và bấm "Thử lại".',
        ],
        needsReload: false,
    },
    firefox: {
        platform: 'firefox',
        title: 'Firefox',
        site: [
            'Bấm biểu tượng bên trái địa chỉ trang.',
            'Ở dòng "Truy cập vị trí", bấm ✕ để bỏ chặn.',
            'Bấm "Thử lại" và chọn "Cho phép" khi được hỏi.',
        ],
        device: [
            'Bật định vị của máy (trên điện thoại: kéo thanh thông báo xuống, bật "Vị trí").',
            'Cho phép ứng dụng Firefox dùng vị trí trong cài đặt của máy.',
            'Quay lại đây và bấm "Thử lại".',
        ],
        needsReload: false,
    },
    other: {
        platform: 'other',
        title: 'Trình duyệt khác',
        site: [
            'Mở cài đặt của trình duyệt, tìm mục quyền trang web → Vị trí.',
            'Cho phép trang này dùng vị trí.',
            'Quay lại đây và bấm "Thử lại".',
        ],
        device: [
            'Bật định vị của thiết bị.',
            'Cho phép trình duyệt này dùng vị trí trong cài đặt của thiết bị.',
            'Quay lại đây và bấm "Thử lại".',
        ],
        needsReload: true,
    },
};

// Thứ tự hiện trong mục "Thiết bị khác".
export const LOCATION_HELP_ORDER: BrowserPlatform[] = [
    'android-chrome', 'ios-safari', 'ios-chrome', 'desktop-chrome', 'mac-safari', 'firefox', 'in-app',
];

export type LocationProblem =
    | 'in-app'          // mở trong Zalo/Facebook
    | 'site-blocked'    // TH2: lỡ bấm "Không cho phép"
    | 'device-blocked'  // TH1: máy không cho trình duyệt dùng vị trí
    | 'blocked-unknown' // bị từ chối nhưng trình duyệt không cho biết là TH1 hay TH2
    | 'device-off'      // máy tắt định vị hoặc không bắt được tín hiệu
    | 'timeout'         // chờ định vị quá lâu
    | 'unsupported';    // trình duyệt không hỗ trợ định vị

// Trình duyệt chỉ báo chung "bị từ chối". Hỏi thêm trạng thái quyền của trang để đoán:
// trang bị chặn thì là lỡ bấm "Không cho phép"; trang vẫn được phép thì do máy chặn trình duyệt.
export function classifyLocationProblem(
    reason: LocationErrorReason,
    permissionState: PermissionState | null,
    platform: BrowserPlatform,
): LocationProblem {
    if (platform === 'in-app') return 'in-app';
    if (reason === 'unsupported') return 'unsupported';
    if (reason === 'timeout') return 'timeout';
    if (reason === 'unavailable') return 'device-off';
    if (reason === 'denied') {
        if (permissionState === 'denied') return 'site-blocked';
        if (permissionState === 'granted' || permissionState === 'prompt') return 'device-blocked';
        return 'blocked-unknown';
    }
    return 'device-off';
}

// Độ chính xác kém hơn mức này thì gần như chắc chắn máy đang tắt "Vị trí chính xác" hoặc chỉ định vị qua mạng.
export const POOR_ACCURACY_M = 1000;
